/**
 * Utility function to substitute template variables with actual flyer data
 *
 * Supported variables:
 * - {{startDate}} - Flyer start date
 * - {{endDate}} - Flyer end date
 * - {{flyerTitle}} - Title of the flyer
 * - {{effectiveDates}} - Full date range (e.g., "Mar 14 to 16")
 * - {{storeAddress}} - Store address
 * - {{storePhone}} - Store phone number
 */

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Format a date to a readable string (e.g., "Mar 14, 2025")
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string
 */
const formatDate = (date) => {
  if (!date) return '';

  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';

  const month = MONTH_NAMES[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();

  return `${month} ${day}, ${year}`;
};

/**
 * Format effective dates range (e.g., "Mar 14 to 16" or "Mar 14 to Apr 2")
 * @param {Date|string} startDate
 * @param {Date|string} endDate
 * @returns {string} Formatted date range
 */
const formatEffectiveDates = (startDate, endDate) => {
  if (!startDate || !endDate) return '';

  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';

  const startMonth = MONTH_NAMES[start.getMonth()];
  const endMonth = MONTH_NAMES[end.getMonth()];
  const startDay = start.getDate();
  const endDay = end.getDate();

  // Same month
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${startMonth} ${startDay} to ${endDay}`;
  }

  // Different months
  return `${startMonth} ${startDay} to ${endMonth} ${endDay}`;
};

/**
 * Substitute template variables in text with actual flyer data
 * @param {string} text - Text containing {{variable}} placeholders
 * @param {Object} flyerData - Object containing flyer data
 * @param {Date|string} flyerData.start_date - Flyer start date
 * @param {Date|string} flyerData.end_date - Flyer end date
 * @param {string} flyerData.flyer_title - Flyer title
 * @param {string} flyerData.store_address - Store address (optional)
 * @param {string} flyerData.store_phone - Store phone (optional)
 * @returns {string} Text with variables substituted
 */
export const substituteVariables = (text, flyerData) => {
  if (!text || typeof text !== 'string') return text;
  if (!flyerData) return text;

  const variablePattern = /\{\{([^}]+)\}\}/g;

  // Check if text contains any variables
  if (!variablePattern.test(text)) {
    return text;
  }

  // Reset regex lastIndex after test
  variablePattern.lastIndex = 0;

  // Build the substitution map
  const substitutions = {
    startDate: formatDate(flyerData.start_date),
    endDate: formatDate(flyerData.end_date),
    flyerTitle: flyerData.flyer_title || '',
    effectiveDates: formatEffectiveDates(flyerData.start_date, flyerData.end_date),
    storeAddress: flyerData.store_address || '',
    storePhone: flyerData.store_phone || ''
  };

  // Replace all variables
  return text.replace(variablePattern, (match, varName) => {
    const trimmedVarName = varName.trim();
    if (substitutions.hasOwnProperty(trimmedVarName)) {
      return substitutions[trimmedVarName];
    }
    // Return original placeholder if variable not found
    return match;
  });
};

/**
 * Check if text contains template variables
 * @param {string} text - Text to check
 * @returns {boolean} True if text contains variables
 */
export const hasTemplateVariables = (text) => {
  if (!text || typeof text !== 'string') return false;
  const variablePattern = /\{\{[^}]+\}\}/;
  return variablePattern.test(text);
};

export default substituteVariables;
